import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatSSN = (value: string) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');

  if (digits.length === 3 && value.endsWith('-')) return value.slice(0, 4);
  else if (digits.length === 5 && value.endsWith('-')) return value.slice(0, 7);
  else if (digits.length <= 3) return digits;
  else if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  else return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
};

export const formatPhone = (value: string) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');

  if (digits.length === 3 && value.endsWith('-')) return value.slice(0, 4);
  else if (digits.length === 6 && value.endsWith('-')) return value.slice(0, 8);
  else if (digits.length <= 3) return digits;
  else if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  else return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export const formatZip = (value: string) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');

  if (digits.length === 5 && value.endsWith('-')) return value.slice(0, 6);
  else if (digits.length <= 5) {
    return digits;
  } else return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Convert a length-at-residence entry into whole months.
 *
 * Months are the canonical unit: the core stores this as
 * MONTHSCURRADDRESS, an integer 0-999, and LOS reads it off
 * `lengthAtInMonths` when it books the loan. Years are an input
 * convenience that never reaches storage, so a member may type "2.5
 * years" and we resolve it to 30 months here.
 *
 * Floors rather than rounds, matching the LOS implementation in
 * `_utils/duration-calculations.ts` — the two apps write the same
 * column and must agree on the boundary case (2.55 years is 30 months
 * in both, not 31 in one and 30 in the other).
 *
 * Returns null for anything non-finite or negative so callers can leave
 * the field unset rather than writing a bogus 0.
 */
export const calculateDurationInMonths = (
  length: number | string | null | undefined,
  duration: string
): number | null => {
  const value = typeof length === 'string' ? parseFloat(length) : length;
  if (value == null || !Number.isFinite(value) || value < 0) return null;
  return Math.floor(duration === 'years' ? value * 12 : value);
};

/**
 * Format loan term from months to a readable string.
 * Uses years when the value is a whole number of years ("2 years"),
 * otherwise falls back to months ("30 months") so we avoid awkward
 * fractional-year readings like "2.5 years".
 */
export const formatTerm = (months: number | string): string => {
  const monthValue = typeof months === 'string' ? parseInt(months, 10) : months;
  if (!Number.isFinite(monthValue) || monthValue <= 0) return '0 months';
  if (monthValue % 12 === 0) {
    const years = monthValue / 12;
    return `${years} year${years === 1 ? '' : 's'}`;
  }
  return `${monthValue} months`;
};
